import { Button } from "@/components/ui/button";
import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { getNestedError } from "@/utils/helpers";
import { cn } from "@/lib/utils";

interface FileUploadComponentProps {
  form: any;
  name: string;
  label: string;
  article?: string;
  description: string;
  showOptional?: boolean;
  allowMultiple?: boolean;
  useDefaultDndDescription?: boolean;
  accept?: Record<string, string[]>;
};

function FileUploadComponent(props: FileUploadComponentProps) {
  const { form, name, label, article, description, showOptional, allowMultiple = true, useDefaultDndDescription = false, accept } = props;

  const { t } = useTranslation();

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length === 0) {
      return;
    }

    const existingFiles = form.getValues(name) || [];

    if (allowMultiple) {
      // Allow multiple files if allowMultiple is true
      form.setValue(name, [...existingFiles, ...acceptedFiles]);
    } else {
      // If allowMultiple is false, we limit the accepted files to one.
      form.setValue(name, [acceptedFiles[0]]);
    }
  }, [form, name, allowMultiple]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, multiple: allowMultiple, accept });

  const { formState } = form;
  const uploadedFiles = form.watch(name) || [];

  const error = getNestedError(formState.errors, name);

  const handleRemoveFile = (index: number) => (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const existingFiles = form.getValues(name) || [];
    existingFiles.splice(index, 1);
    form.setValue(name, existingFiles);
  };

  return (
    <div>
      <label className={cn("block text-sm font-semibold", error ? "text-red-500" : "text-slate-800")}>
        {label}
        {/* Show total for each file type */}
        {uploadedFiles.length > 0 && (
          <span className="text-slate-400 font-normal">
            &nbsp;({uploadedFiles.length > 1 ? uploadedFiles.length + " " + t("common:files") : uploadedFiles.length + " " + t("common:file")})
          </span>
        )}
        {showOptional &&
          <span className="text-slate-400 font-normal">&nbsp;({t("optional")})</span>
        }
      </label>
      <div
        {...getRootProps()}
        className="file-upload-inner-container"
      >
        <input {...getInputProps({ multiple: true })} />
        {isDragActive ? (
          <p>{t("modals:aiimport.drophere")}</p>
        ) : (
          <div className="flex flex-col items-center gap-1 h-[120px] overflow-y-auto scrollbar-hide">
            {uploadedFiles.length > 0 ? (
              <>
                <ul className="uploaded-files-list">
                  {uploadedFiles.map((file: File, index: number) => (
                    <li key={index} className="uploaded-file-item flex mb-1 items-center justify-between">
                      <span className={cn("mr-2 break-all", file.name.length > 80 && "text-red-500")}>{file.name}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="xs"
                        className="p-1 text-xs border-red-500 text-red-500 hover:bg-red-500/30 hover:text-red-500"
                        onClick={handleRemoveFile(index)}>
                        {t("common:buttons.remove")}
                      </Button>
                    </li>
                  ))}
                </ul>
                <p className="text-slate-400">{description}</p>
              </>
            ) : (
              <>
                <p className="font-semibold text-slate-800">
                  {allowMultiple
                    ? t("modals:aiimport.dragndrop", { article, filename: useDefaultDndDescription ? "" : label })
                    : t("modals:aiimport.dragndropsingle", { filename: useDefaultDndDescription ? "" : label })}
                </p>
                <p className="text-slate-400">{description}</p>
              </>
            )}
          </div>
        )}
      </div>
      {error && <p className="text-red-500 text-sm">{Array.isArray(error) ? error[0]?.message : error.message}</p>}
    </div>
  );
}

export default FileUploadComponent;
